<script setup>
const { eligibility } = defineProps({
  eligibility: Object,
})
const emit = defineEmits(['update', 'close'])
const { features } = useFeatures()
const { t } = useI18n()
const { r, redirectTo } = useRoutes()

const requiredDocs = computed(() => {
  const prerequisites = eligibility?.prerequisites
  return [
    ...(prerequisites?.forms ? prerequisites.forms : []),
    ...(prerequisites?.waivers ? prerequisites.waivers : []),
  ].flat()
})

const useFormsAndWaiversV2 = features?.waiver_signatures

const completionPath = computed(() => {
  const urlObject = new URL(eligibility?.prerequisites.completion_url)
  const urlParams = new URLSearchParams(urlObject.search)

  urlParams.set('next', window.location.href)
  urlParams.delete('auth0IdToken')

  return r({
    path: `/prerequisites/?${urlParams.toString()}`,
    isV2Path: useFormsAndWaiversV2,
    applyCurrentProviderPrefix: true,
  })
})

function completePrerequisites() {
  emit('close')
  redirectTo(completionPath.value, useFormsAndWaiversV2)
}
</script>

<template>
  <div
    v-if="!step?.custom"
    class="flex grow flex-col justify-between flex-gap-y-4 whitespace-pre-wrap"
  >
    <DSAlert
      type="warning"
      no-close
      class="w-100%"
    >
      {{
        t('In order to register for this class you need to fill out the following forms/waivers:')
      }}
      <ul class="list-disc list-inside pl-4 text-text-600">
        <template
          v-for="(doc, docIndex) in requiredDocs"
          :key="docIndex"
        >
          <li>
            {{ doc.name }}
          </li>
        </template>
      </ul>
    </DSAlert>

    <div class="flex flex-col flex-gap-y-2">
      <DSButton
        class="btn btn-md w-full uppercase btn-fill-primary"
        @click="completePrerequisites"
      >
        <span>{{ t('Continue') }}</span>
      </DSButton>
      <DSButton
        class="btn-md w-full uppercase btn-outline-secondary"
        @click="emit('close')"
      >
        <span>{{ t('Cancel') }}</span>
      </DSButton>
    </div>
  </div>
</template>

<i18n>
{
  "en-us": {
    "In order to register for this class you need to fill out the following forms/waivers:": "In order to register for this class you need to fill out the following forms/waivers:",
    "Continue": "Continue",
    "Cancel": "Cancel"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "In order to register for this class you need to fill out the following forms/waivers:": "Um sich für diesen Kurs anzumelden, müssen Sie die folgenden Formulare/Verzichtserklärungen ausfüllen:",
  "Continue": "Weiter",
  "Cancel": "Abbrechen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "In order to register for this class you need to fill out the following forms/waivers:": "Pour vous inscrire à ce rendez-vous, vous devez remplir les formulaires/attestations ci-après :",
  "Continue": "Continuer",
  "Cancel": "Annuler"
}
</i18n>

<i18n locale="es" lang="json">
{
  "In order to register for this class you need to fill out the following forms/waivers:": "Para inscribirse en esta clase debe completar los próximos formularios o documentos de exención de responsabilidad:",
  "Continue": "Continuar",
  "Cancel": "Cancelar"
}
</i18n>
